import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  BellRing,
  Cctv,
  ConciergeBell,
  DoorOpen,
  PhoneIncoming,
  Router,
  ShieldCheck,
  UserPlus,
} from 'lucide-react';
import { Link } from 'react-router';
import {
  type AccessEventDto,
  type AlertDto,
  type CallDto,
  CALL_STATUS_LABELS,
  type DashboardDto,
  LIVE_EVENTS,
} from '@brimostech/shared';
import { Badge, Button, cn, EmptyState, PageHeader, StatusDot } from '@brimostech/ui';
import { ErrorState } from '@/components/error-state';
import { Section } from '@/components/section';
import { StatCard, StatCardSkeleton } from '@/components/stat-card';
import { AlertList } from '@/features/alerts/alert-list';
import { useCurrentUser } from '@/features/auth/auth-context';
import { firstName, formatRelative } from '@/lib/format';
import { useLiveEvent } from '@/lib/live';
import { useCan } from '@/lib/permissions';
import { queries } from '@/lib/queries';
import { AgendaSection } from './agenda-section';
import { OccupancySection } from './occupancy-section';

const RECENT_EVENTS = 8;
/** Buttons that sit on the brand band. */
const BAND_BUTTON = 'border-0 bg-white/15 text-white shadow-none hover:bg-white/25';

function ratio(online: number, total: number): 'online' | 'offline' | 'pending' | 'neutral' {
  if (total === 0) return 'neutral';
  if (online === total) return 'online';
  return online === 0 ? 'offline' : 'pending';
}

/** Síndico dashboard (5.10): today in numbers, what needs attention and the live feed. */
export function DashboardPage({ tenantId }: { tenantId: string }) {
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const canResolve = useCan('alerts:write');
  const canFrontDesk = useCan('frontdesk:use');
  const canDevices = useCan('devices:read');
  const canRegistry = useCan('registry:read');
  const dashboard = useQuery({ ...queries.dashboard(tenantId), refetchInterval: 30_000 });
  const key = queries.dashboard(tenantId).queryKey;
  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: key });
  }, [queryClient, key]);

  // Global live feed: accesses update the numbers in place, alerts and calls refetch.
  useLiveEvent<AccessEventDto>(
    LIVE_EVENTS.access,
    useCallback(
      (event) => {
        queryClient.setQueryData<DashboardDto>(key, (current) => {
          if (!current) return current;
          const fresh = !current.recentEvents.some((e) => e.id === event.id);
          const today = { ...current.today };
          if (fresh && event.kind === 'door') today.doorsOpened += 1;
          if (fresh && event.kind === 'access' && event.result === 'granted') today.granted += 1;
          if (fresh && event.kind === 'access' && event.result === 'denied') today.denied += 1;
          return {
            ...current,
            today,
            recentEvents: [event, ...current.recentEvents.filter((e) => e.id !== event.id)].slice(
              0,
              RECENT_EVENTS,
            ),
          };
        });
      },
      [queryClient, key],
    ),
  );
  useLiveEvent<AlertDto>(LIVE_EVENTS.alert, invalidate);
  useLiveEvent<CallDto>(LIVE_EVENTS.call, invalidate);

  const data = dashboard.data;
  const condominiumName =
    user.condominiums.find((c) => c.id === tenantId)?.name ?? 'condomínio selecionado';
  const todayLabel = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date());

  return (
    <>
      <PageHeader
        tone="brand"
        className="pb-24"
        title={`Olá, ${firstName(user.name)}`}
        description={`${condominiumName} · ${todayLabel}`}
        actions={
          <>
            {canFrontDesk ? (
              <Button variant="secondary" className={BAND_BUTTON} asChild>
                <Link to="/portaria">
                  <ConciergeBell /> Portaria
                </Link>
              </Button>
            ) : null}
            <Button variant="secondary" className={BAND_BUTTON} asChild>
              <Link to="/eventos">
                <Activity /> Eventos
              </Link>
            </Button>
          </>
        }
      />

      {/* The day's numbers sit over the bottom edge of the band (the same gesture as the app). */}
      <div className="-mt-20 grid gap-4 px-3 sm:grid-cols-2 sm:px-5 xl:grid-cols-4">
        {!data ? (
          Array.from({ length: 4 }).map((_, i) => (
            <StatCardSkeleton key={i} className="shadow-md" />
          ))
        ) : (
          <>
            <StatCard
              className="shadow-md"
              label="Acessos liberados hoje"
              value={data.today.granted}
              icon={ShieldCheck}
              to="/eventos"
              hint={`${data.today.denied} negado(s)`}
            />
            <StatCard
              className="shadow-md"
              label="Portões abertos hoje"
              value={data.today.doorsOpened}
              icon={DoorOpen}
              to="/eventos"
              hint="pelo portal, app, portaria ou chamada"
            />
            <StatCard
              className="shadow-md"
              label="Chamadas hoje"
              value={data.today.calls}
              icon={PhoneIncoming}
              to={canDevices ? '/interfone' : undefined}
              hint={`${data.today.missedCalls} perdida(s)`}
            />
            <StatCard
              className="shadow-md"
              label="Visitantes hoje"
              value={data.today.visitors}
              icon={UserPlus}
              to={canFrontDesk ? '/portaria' : undefined}
              hint={
                data.frontDesk.operatorsOnline > 0
                  ? `${data.frontDesk.operatorsOnline} na tela da portaria`
                  : 'ninguém na tela da portaria'
              }
            />
          </>
        )}
      </div>

      {dashboard.isError ? (
        <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          {data?.occupancy ? (
            <OccupancySection occupancy={data.occupancy} canRegistry={canRegistry} />
          ) : null}
          <Section
            title="Precisa de atenção"
            description="Alertas abertos: gateway, dispositivos, câmeras, portas e sincronização."
            actions={
              data && data.alerts.open > 0 ? (
                <Badge variant={data.alerts.critical > 0 ? 'error' : 'warning'}>
                  <BellRing className="size-3" /> {data.alerts.open}
                </Badge>
              ) : undefined
            }
          >
            <AlertList
              alerts={data?.alerts.items}
              isLoading={!data && dashboard.isPending}
              canResolve={canResolve}
              resolvePath="/alerts"
              emptyDescription="Nenhum alerta aberto. Gateways, dispositivos e câmeras estão respondendo."
            />
            {data && data.alerts.open > data.alerts.items.length ? (
              <Button variant="link" className="px-0" asChild>
                <Link to="/alertas">Ver todos os {data.alerts.open} alertas</Link>
              </Button>
            ) : null}
          </Section>

          <Section
            title="Ao vivo"
            description="Últimos acessos e aberturas, em tempo real."
            actions={
              <Button variant="ghost" size="sm" asChild>
                <Link to="/eventos">Ver todos</Link>
              </Button>
            }
          >
            {!data ? null : data.recentEvents.length === 0 ? (
              <EmptyState
                icon={<Activity />}
                title="Nenhum acesso ainda"
                description="Quando alguém passar por um terminal, aparece aqui."
              />
            ) : (
              <ul className="divide-y text-sm" aria-live="polite">
                {data.recentEvents.map((event, index) => (
                  <li
                    key={event.id}
                    className={cn(
                      'flex items-center gap-3 py-2',
                      index === 0 && 'animate-in fade-in duration-300',
                    )}
                  >
                    <span
                      className={cn(
                        'size-2 shrink-0 rounded-full',
                        event.kind === 'door'
                          ? 'bg-info'
                          : event.result === 'granted'
                            ? 'bg-success'
                            : 'bg-destructive',
                      )}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">
                        {event.personName ?? event.actorName ?? 'Não identificado'}
                        {event.unitLabel ? (
                          <span className="font-normal text-muted-foreground">
                            {' '}
                            · {event.unitLabel}
                          </span>
                        ) : null}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {event.kind === 'door'
                          ? 'Abertura'
                          : event.result === 'granted'
                            ? 'Liberado'
                            : `Negado${event.reason ? ` · ${event.reason}` : ''}`}
                        {' · '}
                        {event.accessPointName ?? event.deviceName ?? '—'}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatRelative(event.occurredAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div className="space-y-4">
          <Section title="Saúde do condomínio" description="O que o gateway está reportando.">
            {!data ? null : (
              <ul className="space-y-3 text-sm">
                <HealthRow
                  icon={Router}
                  label="Gateways"
                  online={data.gateways.online}
                  total={data.gateways.total}
                />
                <HealthRow
                  icon={DoorOpen}
                  label="Terminais e relés"
                  online={data.devices.online}
                  total={data.devices.total}
                />
                <HealthRow
                  icon={Cctv}
                  label="Câmeras"
                  online={data.cameras.online}
                  total={data.cameras.total}
                />
                <li className="flex items-center gap-3">
                  <StatusDot
                    status={
                      data.sync.error > 0 ? 'error' : data.sync.pending > 0 ? 'pending' : 'online'
                    }
                  />
                  <span className="flex-1">Sincronização dos terminais</span>
                  <span className="text-muted-foreground">
                    {data.sync.pending === 0 && data.sync.error === 0
                      ? 'em dia'
                      : `${data.sync.pending} pendente(s) · ${data.sync.error} erro(s)`}
                  </span>
                </li>
              </ul>
            )}
          </Section>

          {data?.agenda ? (
            <AgendaSection agenda={data.agenda} packages={data.packages} finance={data.finance} />
          ) : null}

          <Section
            title="Câmeras com problema"
            description="Offline ou com erro na última sondagem."
          >
            {!data ? null : data.cameras.total === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma câmera cadastrada.</p>
            ) : data.cameras.problems.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Todas as câmeras ativas estão respondendo.
              </p>
            ) : (
              <ul className="divide-y text-sm">
                {data.cameras.problems.map((camera) => (
                  <li key={camera.id} className="flex items-center gap-3 py-2">
                    <StatusDot status={camera.status === 'error' ? 'error' : 'offline'} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{camera.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {camera.lastError ??
                          (camera.status === 'error' ? 'Erro na sondagem' : 'Sem resposta')}
                        {camera.lastSeenAt ? ` · vista ${formatRelative(camera.lastSeenAt)}` : ''}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/cameras">Abrir</Link>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            title="Últimas chamadas"
            description="Interfone: quem chamou e como terminou."
            actions={
              canDevices ? (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/interfone">Histórico</Link>
                </Button>
              ) : undefined
            }
          >
            {!data ? null : data.recentCalls.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma chamada registrada.</p>
            ) : (
              <ul className="divide-y text-sm">
                {data.recentCalls.map((call) => (
                  <li key={call.id} className="flex items-center gap-3 py-2">
                    <PhoneIncoming
                      className={cn(
                        'size-4',
                        call.status === 'missed' ? 'text-destructive' : 'text-muted-foreground',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">
                        {call.stationName}
                        {call.unitLabel ? (
                          <span className="font-normal text-muted-foreground">
                            {' '}
                            → {call.unitLabel}
                          </span>
                        ) : null}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {CALL_STATUS_LABELS[call.status]}
                        {call.answeredByName ? ` por ${call.answeredByName}` : ''}
                        {call.openedAt ? ' · portão aberto' : ''}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatRelative(call.startedAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </>
  );
}

function HealthRow({
  icon: Icon,
  label,
  online,
  total,
}: {
  icon: typeof Router;
  label: string;
  online: number;
  total: number;
}) {
  return (
    <li className="flex items-center gap-3">
      <StatusDot status={ratio(online, total)} pulse={total > 0 && online === total} />
      <Icon className="size-4 text-muted-foreground" />
      <span className="flex-1">{label}</span>
      <span className="text-muted-foreground">
        {total === 0 ? 'nenhum' : `${online} de ${total} online`}
      </span>
    </li>
  );
}
